import { styled } from '@mui/material/styles';
import { Check } from 'lucide-react';
import { GlIcon, MIcon, PyIcon, RxIcon, SadIcon } from '@/assets/icons/tickmarks';

/** The six platform tickmarks, which ship as icons (numbered notes use `NoteBadge`). */
export type TickmarkGlyphKind = 'check' | 'gl' | 'm' | 'rx' | 'sad' | 'py';

interface TickmarkGlyphProps {
  kind: TickmarkGlyphKind;
  /** Fill/stroke colour (default meanings differ per glyph; caller resolves it). */
  color: string;
  size?: number;
  'data-testid'?: string;
}

/**
 * Renders one audit tickmark glyph (✓ / GL / M / Rx / SAD / PY) at the given colour.
 * Shared by the Editor annotation tools and the Data-analysis marker picker so the
 * two surfaces are pixel-identical (Figma 36760-157545).
 */
export function TickmarkGlyph({ kind, color, size = 16, ...rest }: TickmarkGlyphProps) {
  const testId = rest['data-testid'];
  if (kind === 'check') {
    return <Check size={size} color={color} strokeWidth={2.5} aria-hidden data-testid={testId} />;
  }
  if (kind === 'gl') {
    return <GlIcon size={size} color={color} strokeWidth={2.5} aria-hidden data-testid={testId} />;
  }
  if (kind === 'm') return <MIcon color={color} width={size} height={size} />;
  if (kind === 'rx') return <RxIcon color={color} width={size} height={size} />;
  if (kind === 'py') return <PyIcon color={color} width={size} height={size} />;
  return <SadIcon color={color} width={size} height={size} />;
}

interface NoteBadgeProps {
  /** The number/symbol shown inside the circle. */
  label: string;
  /** Circle fill (numbered notes are red in the design). */
  color: string;
  size?: number;
  'data-testid'?: string;
}

/**
 * A numbered-note badge: a filled circle with a white number (Figma). The fill
 * `color` and `size` are the only per-instance dynamic bits, so they ride in as
 * `styled` props — the layout lives in the theme-aware styled component, not an
 * inline `style` object.
 */
const Badge = styled('span', {
  shouldForwardProp: (prop) => prop !== 'badgeColor' && prop !== 'badgeSize',
})<{ badgeColor: string; badgeSize: number }>(({ theme, badgeColor, badgeSize }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: badgeSize,
  height: badgeSize,
  padding: '0 3px',
  borderRadius: badgeSize,
  backgroundColor: badgeColor,
  color: theme.palette.common.white,
  fontSize: badgeSize <= 16 ? 10 : 13,
  fontWeight: 700,
  lineHeight: 1,
  flexShrink: 0,
  boxSizing: 'border-box',
}));

export function NoteBadge({ label, color, size = 16, ...rest }: NoteBadgeProps) {
  return (
    <Badge badgeColor={color} badgeSize={size} data-testid={rest['data-testid']}>
      {label}
    </Badge>
  );
}
